import { useMemo } from 'react';
import { Col, Row, Card, Button } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';

import LinkContainer from '../components/link_container';
import { renderOverlay, renderOffline, renderError } from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  supplierCatalogListPageQuery,
  supplierCatalogDelete as supplierCatalogDeleteMutation,
} from '../graphql/supplier_catalog_queries';
import { settingsSet } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const SupplierCatalogList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [supplierCatalogDelete] = useMutation(supplierCatalogDeleteMutation);

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(supplierCatalogListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ supplierCatalogList }) => {
      // write an individual query for every supplierCatalog in the list
      updateFunctions.supplierCatalogList(pageClient, supplierCatalogList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const supplierCatalogDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const supplierCatalogId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: supplierCatalogId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'SupplierCatalogType',
        recordId: supplierCatalogId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.supplierCatalogDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'supplierCatalogDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await supplierCatalogDelete(mutationData);
        toastSuccess('Supplier Catalog delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Supplier Catalog delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      supplierCatalogDelete(mutationData);
      toastWarning(`Supplier Catalog delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const getSupplierName = (row) => {
    const {
      supplier: { name: supplierName },
    } = row;
    return supplierName;
  };

  const parentColumns = [
    {
      header: 'Supplier',
      id: 'supplier',
      accessorFn: (row) => getSupplierName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Name',
      accessorKey: 'name',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col sm={12}>
          <div className="float-none">
            <div className="float-start">
              <h1 className="h3 mb-3">Supplier Catalogs</h1>
            </div>
            <div className="float-end">
              <Button
                className="me-2"
                variant="primary"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                Refresh
              </Button>
              <LinkContainer to="/supplier_catalogs/new">
                <Button variant="primary">New Supplier Catalog</Button>
              </LinkContainer>
            </div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName="supplierCatalog"
                parentColumns={parentColumns}
                data={pageData.supplierCatalogList}
                doShow={false}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={supplierCatalogDeleteClicked}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default SupplierCatalogList;
